package org.teddapp.project.ui.focus

import org.teddapp.project.ui.LocalTeddFonts

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.slideInHorizontally
import androidx.compose.animation.slideInVertically
import androidx.compose.animation.slideOutHorizontally
import androidx.compose.animation.slideOutVertically
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.tedd.db.Pomodoro_check
import com.tedd.db.Pomodoro_item
import com.tedd.db.Pomodoro_session
import com.tedd.db.Task
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.db.nowMillis
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddLogo
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.nav.Section
import org.teddapp.project.ui.pomodoro.POM_PRESET_DURATIONS
import org.teddapp.project.ui.pomodoro.POM_TODAY_ISO
import org.teddapp.project.ui.pomodoro.PomPriority
import org.teddapp.project.ui.pomodoro.formatMmSs
import org.teddapp.project.ui.pomodoro.isoAddDays
import org.teddapp.project.ui.pomodoro.resolveItemTitle

// ──────────────────────────────────────────────────────────────────────
// Parties propres à la mise en page compacte (téléphone) : en-tête, lignes,
// historique, feuilles de bas d'écran.
// ──────────────────────────────────────────────────────────────────────

@Composable
internal fun FocusCompactHeader(
    timerMinutes: Int,
    onOpenSettings: () -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .statusBarsPadding()
            .padding(start = 16.dp, end = 16.dp, top = 14.dp, bottom = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        TeddLogo(size = 28.dp)
        Column(modifier = Modifier.weight(1f)) {
            Text(
                "POMODORO",
                color = TeddTokens.InkMuted,
                fontSize = 10.sp,
                fontWeight = FontWeight.Bold,
                letterSpacing = 0.6.sp,
            )
            Spacer(modifier = Modifier.height(2.dp))
            Text(
                "Aujourd'hui · $timerMinutes min",
                style = TextStyle(
                    color = TeddTokens.Ink,
                    fontSize = 18.sp,
                    fontWeight = FontWeight.Bold,
                    letterSpacing = (-0.3).sp,
                ),
            )
        }
        Box(
            modifier = Modifier
                .size(36.dp)
                .clip(CircleShape)
                .background(TeddTokens.SurfaceSunken, CircleShape)
                .border(1.dp, TeddTokens.Line, CircleShape)
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onOpenSettings,
                ),
            contentAlignment = Alignment.Center,
        ) {
            TeddIcon(name = "settings", size = 15.dp, color = TeddTokens.InkMuted)
        }
    }
}

@Composable
internal fun FocusCompactItemRow(
    item: Pomodoro_item,
    meta: ItemMeta,
    verifsDone: Int,
    verifsTotal: Int,
    onToggle: () -> Unit,
    onOpenInspector: () -> Unit,
    onMoveUp: (() -> Unit)? = null,
    onMoveDown: (() -> Unit)? = null,
) {
    val prio = PomPriority.fromValue(item.priority)
    val done = item.completed != 0L
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(TeddTokens.RSm))
            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = onOpenInspector,
            )
            .padding(horizontal = 12.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        Box(
            modifier = Modifier
                .size(22.dp)
                .clip(RoundedCornerShape(TeddTokens.RXs))
                .then(
                    if (done) Modifier.background(TeddTokens.SignatureGradient, RoundedCornerShape(TeddTokens.RXs))
                    else Modifier.border(1.5.dp, prio.color, RoundedCornerShape(TeddTokens.RXs))
                )
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onToggle,
                ),
            contentAlignment = Alignment.Center,
        ) {
            if (done) TeddIcon(name = "check", size = 13.dp, color = TeddTokens.OnAction, strokeWidth = 3.dp)
        }
        Box(
            modifier = Modifier
                .size(20.dp)
                .clip(RoundedCornerShape(TeddTokens.RXs))
                .background(prio.color.copy(alpha = 0.12f), RoundedCornerShape(TeddTokens.RXs)),
            contentAlignment = Alignment.Center,
        ) {
            TeddIcon(name = prio.icon, size = 11.dp, color = prio.color, strokeWidth = 2.2.dp)
        }
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = meta.title,
                color = if (done) TeddTokens.InkMuted else TeddTokens.Ink,
                fontSize = 13.sp,
                fontWeight = FontWeight.SemiBold,
                maxLines = 1,
                textDecoration = if (done) TextDecoration.LineThrough else TextDecoration.None,
            )
            Spacer(modifier = Modifier.height(2.dp))
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(5.dp)) {
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(3.dp))
                        .background(meta.color.copy(alpha = 0.12f), RoundedCornerShape(3.dp))
                        .padding(horizontal = 5.dp, vertical = 1.dp),
                ) {
                    Text(
                        text = meta.sourceLabel,
                        color = meta.color,
                        fontSize = 9.sp,
                        fontWeight = FontWeight.Bold,
                        letterSpacing = 0.4.sp,
                    )
                }
                ItemMinutesTag(meta.minutes, done)
                if (meta.context.isNotEmpty()) {
                    Text(
                        text = meta.context,
                        color = TeddTokens.InkMuted,
                        fontSize = 10.5.sp,
                        maxLines = 1,
                        modifier = Modifier.weight(1f),
                    )
                } else {
                    Spacer(modifier = Modifier.weight(1f))
                }
                if (verifsTotal > 0) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(2.dp),
                    ) {
                        TeddIcon(name = "check", size = 9.dp, color = TeddTokens.InkMuted, strokeWidth = 2.4.dp)
                        Text(
                            text = "$verifsDone/$verifsTotal",
                            color = TeddTokens.InkMuted,
                            fontSize = 10.sp,
                            fontFamily = LocalTeddFonts.current.mono,
                        )
                    }
                }
            }
        }
        FocusMoveArrows(onMoveUp = onMoveUp, onMoveDown = onMoveDown)
        TeddIcon(name = "chevron", size = 12.dp, color = TeddTokens.InkMuted)
    }
}

// ──────────────────────────────────────────────────────────────────────
// History tab — list of past sessions cards
// ──────────────────────────────────────────────────────────────────────

@Composable
internal fun FocusCompactHistory(
    sessions: List<Pomodoro_session>,
    repos: TeddRepositories,
    resolveMeta: (Pomodoro_item) -> ItemMeta,
    onClone: (String) -> Unit,
    modifier: Modifier = Modifier,
) {
    if (sessions.isEmpty()) {
        Column(
            modifier = modifier.fillMaxSize().padding(28.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center,
        ) {
            Box(
                modifier = Modifier
                    .size(48.dp)
                    .clip(RoundedCornerShape(TeddTokens.RMd))
                    .background(TeddTokens.LimeSoft, RoundedCornerShape(TeddTokens.RMd)),
                contentAlignment = Alignment.Center,
            ) {
                TeddIcon(name = "clock", size = 22.dp, color = TeddTokens.LimeStrong, strokeWidth = 1.6.dp)
            }
            Spacer(modifier = Modifier.height(12.dp))
            Text("Pas encore d'historique", color = TeddTokens.Ink, fontSize = 14.sp, fontWeight = FontWeight.SemiBold)
            Spacer(modifier = Modifier.height(3.dp))
            Text("Tes sessions passées apparaîtront ici.", color = TeddTokens.InkMuted, fontSize = 12.sp)
        }
        return
    }
    LazyColumn(
        modifier = modifier.fillMaxSize(),
        contentPadding = PaddingValues(horizontal = 12.dp, vertical = 12.dp),
        verticalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        items(sessions, key = { it.id }) { s ->
            FocusCompactHistoryCard(session = s, repos = repos, resolveMeta = resolveMeta, onClone = { onClone(s.id) })
        }
    }
}

@Composable
internal fun FocusCompactHistoryCard(
    session: Pomodoro_session,
    repos: TeddRepositories,
    resolveMeta: (Pomodoro_item) -> ItemMeta,
    onClone: () -> Unit,
) {
    val items by repos.pomodoroItems.observeBySession(session.id).collectAsState(emptyList())
    val total = items.size
    val done = items.count { it.completed != 0L }
    val pct = if (total == 0) 0 else done * 100 / total

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(TeddTokens.RSm))
            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm)),
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 12.dp, vertical = 10.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp),
        ) {
            Box(
                modifier = Modifier
                    .size(34.dp)
                    .clip(RoundedCornerShape(TeddTokens.RSm))
                    .background(TeddTokens.SignatureGradient, RoundedCornerShape(TeddTokens.RSm)),
                contentAlignment = Alignment.Center,
            ) {
                Text(
                    "$pct%",
                    color = TeddTokens.OnAction,
                    fontSize = 11.sp,
                    fontWeight = FontWeight.Bold,
                    fontFamily = LocalTeddFonts.current.mono,
                )
            }
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = session.date_start,
                    color = TeddTokens.Ink,
                    fontSize = 13.sp,
                    fontWeight = FontWeight.SemiBold,
                )
                Text(
                    text = "$done/$total · ${session.timer_minutes} min" +
                        if (session.status == "completed") " · clôturée" else "",
                    color = TeddTokens.InkMuted,
                    fontSize = 10.5.sp,
                    fontFamily = LocalTeddFonts.current.mono,
                )
            }
            TeddButton(
                text = "Cloner",
                onClick = onClone,
                kind = TeddButtonKind.Secondary,
                size = TeddButtonSize.Sm,
            )
        }
        Column(modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp)) {
            items.sortedBy { it.order_index }.forEach { it ->
                val meta = resolveMeta(it)
                val itDone = it.completed != 0L
                val prio = PomPriority.fromValue(it.priority)
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 4.dp, vertical = 4.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    Box(
                        modifier = Modifier
                            .size(13.dp)
                            .clip(RoundedCornerShape(3.dp))
                            .then(
                                if (itDone) Modifier.background(TeddTokens.SignatureGradient, RoundedCornerShape(3.dp))
                                else Modifier.border(1.5.dp, prio.color, RoundedCornerShape(3.dp))
                            ),
                        contentAlignment = Alignment.Center,
                    ) {
                        if (itDone) TeddIcon(name = "check", size = 8.dp, color = TeddTokens.OnAction, strokeWidth = 3.dp)
                    }
                    Box(
                        modifier = Modifier
                            .clip(RoundedCornerShape(3.dp))
                            .background(meta.color.copy(alpha = 0.12f), RoundedCornerShape(3.dp))
                            .padding(horizontal = 4.dp, vertical = 1.dp),
                    ) {
                        Text(
                            text = meta.sourceLabel,
                            color = meta.color,
                            fontSize = 9.sp,
                            fontWeight = FontWeight.Bold,
                            letterSpacing = 0.4.sp,
                        )
                    }
                    ItemMinutesTag(meta.minutes, itDone, fontSize = 10.sp)
                    Text(
                        text = meta.title,
                        color = if (itDone) TeddTokens.InkMuted else TeddTokens.Ink,
                        fontSize = 11.5.sp,
                        fontWeight = FontWeight.Medium,
                        textDecoration = if (itDone) TextDecoration.LineThrough else TextDecoration.None,
                        modifier = Modifier.weight(1f),
                        maxLines = 1,
                    )
                    Box(modifier = Modifier.size(6.dp).clip(CircleShape).background(prio.color, CircleShape))
                }
            }
        }
    }
}

// ──────────────────────────────────────────────────────────────────────
// Bottom sheets
// ──────────────────────────────────────────────────────────────────────


@Composable
internal fun FocusSourceGroupHeader(label: String, color: Color, icon: String, count: Int) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(start = 8.dp, end = 8.dp, top = 12.dp, bottom = 6.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Box(
            modifier = Modifier
                .size(20.dp)
                .clip(RoundedCornerShape(TeddTokens.RXs))
                .background(color.copy(alpha = 0.12f), RoundedCornerShape(TeddTokens.RXs)),
            contentAlignment = Alignment.Center,
        ) {
            TeddIcon(name = icon, size = 11.dp, color = color, strokeWidth = 2.dp)
        }
        Text(
            label.uppercase(),
            color = color,
            fontSize = 11.sp,
            fontWeight = FontWeight.Bold,
            letterSpacing = 0.5.sp,
            modifier = Modifier.weight(1f),
        )
        Text(
            count.toString(),
            color = TeddTokens.InkMuted,
            fontSize = 11.sp,
            fontFamily = LocalTeddFonts.current.mono,
        )
    }
}

@Composable
internal fun FocusSettingsSheet(
    currentMinutes: Int,
    hasExtension: Boolean,
    canTerminate: Boolean,
    onPickMinutes: (Int) -> Unit,
    onTerminate: () -> Unit,
    onExtend3Days: () -> Unit,
    onCancelExtension: () -> Unit,
    onClose: () -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .verticalScroll(rememberScrollState())
            .padding(horizontal = 22.dp, vertical = 16.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(
                "Paramètres de la session",
                color = TeddTokens.Ink,
                fontSize = 16.sp,
                fontWeight = FontWeight.SemiBold,
                modifier = Modifier.weight(1f),
            )
            Box(
                modifier = Modifier
                    .size(30.dp)
                    .clip(RoundedCornerShape(TeddTokens.RXs))
                    .clickable(
                        interactionSource = remember { MutableInteractionSource() },
                        indication = null,
                        onClick = onClose,
                    )
                    .rotate(45f),
                contentAlignment = Alignment.Center,
            ) {
                TeddIcon(name = "plus", size = 14.dp, color = TeddTokens.InkMuted, strokeWidth = 1.8.dp)
            }
        }
        Spacer(modifier = Modifier.height(16.dp))
        Text(
            "DURÉE DU MINUTEUR",
            color = TeddTokens.InkMuted,
            fontSize = 10.sp,
            fontWeight = FontWeight.Bold,
            letterSpacing = 0.5.sp,
        )
        Spacer(modifier = Modifier.height(8.dp))
        androidx.compose.foundation.layout.FlowRow(
            horizontalArrangement = Arrangement.spacedBy(6.dp),
            verticalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            POM_PRESET_DURATIONS.forEach { m ->
                val on = m == currentMinutes
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(TeddTokens.RSm))
                        .background(if (on) TeddTokens.LimeSoft else Color.Transparent, RoundedCornerShape(TeddTokens.RSm))
                        .border(
                            1.dp,
                            if (on) TeddTokens.LimeStrong else TeddTokens.Line,
                            RoundedCornerShape(TeddTokens.RSm),
                        )
                        .clickable(
                            interactionSource = remember { MutableInteractionSource() },
                            indication = null,
                            onClick = { onPickMinutes(m) },
                        )
                        .padding(horizontal = 12.dp, vertical = 7.dp),
                ) {
                    Text(
                        "$m min",
                        color = if (on) TeddTokens.LimeStrong else TeddTokens.InkMuted,
                        fontSize = 12.sp,
                        fontWeight = FontWeight.SemiBold,
                        fontFamily = LocalTeddFonts.current.mono,
                    )
                }
            }
        }
        Spacer(modifier = Modifier.height(20.dp))
        Text(
            "JOURNÉE",
            color = TeddTokens.InkMuted,
            fontSize = 10.sp,
            fontWeight = FontWeight.Bold,
            letterSpacing = 0.5.sp,
        )
        Spacer(modifier = Modifier.height(8.dp))
        if (canTerminate) {
            TeddButton(
                text = "Terminer la journée",
                onClick = onTerminate,
                kind = TeddButtonKind.Primary,
                size = TeddButtonSize.Md,
                leadingIcon = { TeddIcon(name = "check", size = 12.dp, color = TeddTokens.OnAction, strokeWidth = 2.4.dp) },
            )
            Spacer(modifier = Modifier.height(6.dp))
        }
        TeddButton(
            text = if (hasExtension) "Annuler l'extension" else "Étendre (+3 jours)",
            onClick = if (hasExtension) onCancelExtension else onExtend3Days,
            kind = TeddButtonKind.Secondary,
            size = TeddButtonSize.Md,
            leadingIcon = { TeddIcon(name = "calendar", size = 12.dp, color = TeddTokens.InkMuted) },
        )
        Spacer(modifier = Modifier.height(20.dp))
    }
}

@Composable
internal fun FocusInspectorSheet(
    item: Pomodoro_item,
    meta: ItemMeta,
    checks: List<Pomodoro_check>,
    onToggleCheck: (id: String, checked: Boolean) -> Unit,
    onAddCheck: (label: String) -> Unit,
    onRemoveCheck: (id: String) -> Unit,
    onChangePriority: (PomPriority) -> Unit,
    onRemove: () -> Unit,
    onClose: () -> Unit,
    onEdit: (() -> Unit)? = null,
) {
    var draft by remember(item.id) { mutableStateOf("") }
    val done = checks.count { it.checked != 0L }
    val total = checks.size
    val prio = PomPriority.fromValue(item.priority)

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .verticalScroll(rememberScrollState())
            .padding(horizontal = 22.dp, vertical = 14.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Box(
                modifier = Modifier
                    .clip(RoundedCornerShape(3.dp))
                    .background(meta.color.copy(alpha = 0.12f), RoundedCornerShape(3.dp))
                    .padding(horizontal = 5.dp, vertical = 1.dp),
            ) {
                Text(meta.sourceLabel, color = meta.color, fontSize = 9.sp, fontWeight = FontWeight.Bold, letterSpacing = 0.4.sp)
            }
            Spacer(modifier = Modifier.width(6.dp))
            ItemMinutesTag(meta.minutes, fontSize = 11.sp)
            Spacer(modifier = Modifier.weight(1f))
            Box(
                modifier = Modifier
                    .size(30.dp)
                    .clip(RoundedCornerShape(TeddTokens.RXs))
                    .clickable(
                        interactionSource = remember { MutableInteractionSource() },
                        indication = null,
                        onClick = onClose,
                    )
                    .rotate(45f),
                contentAlignment = Alignment.Center,
            ) {
                TeddIcon(name = "plus", size = 14.dp, color = TeddTokens.InkMuted, strokeWidth = 1.8.dp)
            }
        }
        Spacer(modifier = Modifier.height(4.dp))
        Text(meta.title, color = TeddTokens.Ink, fontSize = 17.sp, fontWeight = FontWeight.Bold)
        if (meta.context.isNotEmpty()) {
            Spacer(modifier = Modifier.height(2.dp))
            Text(meta.context, color = TeddTokens.InkMuted, fontSize = 12.sp)
        }
        Spacer(modifier = Modifier.height(14.dp))

        // Priority picker
        Spacer(modifier = Modifier.height(16.dp))
        Text(
            "PRIORITÉ",
            color = TeddTokens.InkMuted,
            fontSize = 10.sp,
            fontWeight = FontWeight.Bold,
            letterSpacing = 0.5.sp,
        )
        Spacer(modifier = Modifier.height(8.dp))
        androidx.compose.foundation.layout.FlowRow(
            horizontalArrangement = Arrangement.spacedBy(6.dp),
            verticalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            PomPriority.entries.forEach { p ->
                val on = p == prio
                Row(
                    modifier = Modifier
                        .clip(RoundedCornerShape(TeddTokens.RFull))
                        .background(
                            if (on) p.color.copy(alpha = 0.1f) else TeddTokens.Surface,
                            RoundedCornerShape(TeddTokens.RFull),
                        )
                        .border(
                            1.dp,
                            if (on) p.color else TeddTokens.Line,
                            RoundedCornerShape(TeddTokens.RFull),
                        )
                        .clickable(
                            interactionSource = remember { MutableInteractionSource() },
                            indication = null,
                            onClick = { onChangePriority(p) },
                        )
                        .padding(horizontal = 10.dp, vertical = 6.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(5.dp),
                ) {
                    Box(modifier = Modifier.size(8.dp).clip(RoundedCornerShape(2.dp)).background(p.color))
                    Text(p.label, color = if (on) p.color else TeddTokens.Ink, fontSize = 11.5.sp, fontWeight = if (on) FontWeight.SemiBold else FontWeight.Medium)
                }
            }
        }

        // Verifications
        Spacer(modifier = Modifier.height(20.dp))
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(
                "VÉRIFICATIONS",
                color = TeddTokens.InkMuted,
                fontSize = 10.sp,
                fontWeight = FontWeight.Bold,
                letterSpacing = 0.5.sp,
                modifier = Modifier.weight(1f),
            )
            if (total > 0) {
                Text(
                    "$done/$total",
                    color = TeddTokens.InkMuted,
                    fontSize = 11.sp,
                    fontFamily = LocalTeddFonts.current.mono,
                    fontWeight = FontWeight.Medium,
                )
            }
        }
        Spacer(modifier = Modifier.height(8.dp))
        if (checks.isEmpty()) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(TeddTokens.RSm))
                    .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                    .padding(10.dp),
            ) {
                Text("Ajoute des critères pour valider avant de cocher.", color = TeddTokens.InkMuted, fontSize = 11.5.sp)
            }
        } else {
            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                checks.forEach { c ->
                    val checked = c.checked != 0L
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(vertical = 2.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                    ) {
                        Box(
                            modifier = Modifier
                                .size(18.dp)
                                .clip(RoundedCornerShape(TeddTokens.RXs))
                                .then(
                                    if (checked) Modifier.background(TeddTokens.SignatureGradient, RoundedCornerShape(TeddTokens.RXs))
                                    else Modifier.border(1.5.dp, TeddTokens.InkSubtle, RoundedCornerShape(TeddTokens.RXs))
                                )
                                .clickable(
                                    interactionSource = remember { MutableInteractionSource() },
                                    indication = null,
                                    onClick = { onToggleCheck(c.id, !checked) },
                                ),
                            contentAlignment = Alignment.Center,
                        ) {
                            if (checked) TeddIcon(name = "check", size = 10.dp, color = TeddTokens.OnAction, strokeWidth = 3.dp)
                        }
                        Text(
                            c.label,
                            color = if (checked) TeddTokens.InkMuted else TeddTokens.Ink,
                            fontSize = 12.5.sp,
                            textDecoration = if (checked) TextDecoration.LineThrough else TextDecoration.None,
                            modifier = Modifier.weight(1f),
                        )
                        Box(
                            modifier = Modifier
                                .size(22.dp)
                                .clip(RoundedCornerShape(TeddTokens.RXs))
                                .clickable(
                                    interactionSource = remember { MutableInteractionSource() },
                                    indication = null,
                                    onClick = { onRemoveCheck(c.id) },
                                ),
                            contentAlignment = Alignment.Center,
                        ) {
                            TeddIcon(name = "trash", size = 11.dp, color = TeddTokens.InkMuted)
                        }
                    }
                }
            }
        }
        Spacer(modifier = Modifier.height(8.dp))
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(TeddTokens.RSm))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                .padding(horizontal = 10.dp, vertical = 7.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            TeddIcon(name = "plus", size = 12.dp, color = TeddTokens.InkMuted)
            BasicTextField(
                value = draft,
                onValueChange = { draft = it },
                modifier = Modifier.weight(1f),
                singleLine = true,
                textStyle = TextStyle(color = TeddTokens.Ink, fontSize = 12.sp),
                cursorBrush = SolidColor(TeddTokens.LimeStrong),
                decorationBox = { inner ->
                    if (draft.isEmpty()) Text("Ajouter une vérification…", color = TeddTokens.InkMuted, fontSize = 12.sp)
                    inner()
                },
            )
            TeddButton(
                text = "+",
                onClick = {
                    val t = draft.trim()
                    if (t.isNotEmpty()) {
                        onAddCheck(t); draft = ""
                    }
                },
                kind = TeddButtonKind.Secondary,
                size = TeddButtonSize.Sm,
                enabled = draft.trim().isNotEmpty(),
            )
        }

        Spacer(modifier = Modifier.height(20.dp))
        if (onEdit != null && item.source_type == "quick") {
            TeddButton(
                text = "Modifier la tâche", onClick = onEdit, kind = TeddButtonKind.Secondary, size = TeddButtonSize.Md,
                modifier = Modifier.fillMaxWidth(),
            )
            Spacer(modifier = Modifier.height(8.dp))
        }
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(TeddTokens.RSm))
                .background(TeddTokens.DangerSoft, RoundedCornerShape(TeddTokens.RSm))
                .border(1.dp, TeddTokens.Danger.copy(alpha = 0.3f), RoundedCornerShape(TeddTokens.RSm))
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onRemove,
                )
                .padding(vertical = 10.dp),
            contentAlignment = Alignment.Center,
        ) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                TeddIcon(name = "trash", size = 12.dp, color = TeddTokens.Danger, strokeWidth = 2.dp)
                Text("Retirer de la session", color = TeddTokens.Danger, fontSize = 12.5.sp, fontWeight = FontWeight.SemiBold)
            }
        }
        Spacer(modifier = Modifier.height(12.dp))
    }
}

// ──────────────────────────────────────────────────────────────────────
// Sort helper (mirror du Mac)
// ──────────────────────────────────────────────────────────────────────
